import React, { useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { IconButton, TextField, Typography, InputBase } from '@mui/material';
import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';

// Client-side caps, mirroring the server's (authoritative) limits — shared by
// the popover, the ProfileDialog pane, and the save-from-message action.
export const QUICK_REPLY_MAX = 20;
export const QUICK_REPLY_MAX_LEN = 500;

// Only show the search field once the list is long enough for it to help.
const SEARCH_THRESHOLD = 5;

const useStyles = makeStyles()((theme) => ({
  root: {
    width: 320,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1),
  },
  searchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(0.25, 0.75),
    marginBottom: theme.spacing(0.75),
    borderRadius: 6,
    backgroundColor: theme.palette.custom.panelRaised,
    border: `1px solid ${theme.palette.custom.line}`,
    '& svg': { fontSize: '1rem', color: theme.palette.text.secondary },
  },
  searchInput: {
    flex: 1,
    fontSize: '0.85rem',
    color: theme.palette.text.primary,
  },
  list: {
    maxHeight: 260,
    overflowY: 'auto',
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  row: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    borderRadius: 6,
    '&:hover': {
      backgroundColor: theme.palette.custom.accentWash,
    },
    // Reveal the per-row delete X on hover only.
    [`&:hover .qr-delete`]: {
      opacity: 1,
    },
  },
  rowText: {
    flex: 1,
    minWidth: 0,
    border: 'none',
    background: 'transparent',
    color: theme.palette.text.primary,
    fontFamily: 'inherit',
    fontSize: '0.85rem',
    textAlign: 'left',
    cursor: 'pointer',
    padding: theme.spacing(0.75, 4, 0.75, 0.75),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  deleteBtn: {
    position: 'absolute',
    right: 2,
    opacity: 0,
    transition: 'opacity 120ms',
    color: theme.palette.text.secondary,
    padding: 2,
  },
  empty: {
    padding: theme.spacing(1, 0.75),
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
  addRow: {
    marginTop: theme.spacing(0.75),
  },
  error: {
    fontSize: '0.72rem',
    color: theme.palette.error.main,
    marginTop: theme.spacing(0.5),
  },
  hint: {
    display: 'block',
    marginTop: theme.spacing(0.75),
    color: theme.palette.text.secondary,
  },
}));

/**
 * Lightweight Quick Replies popover body (opened from ChatPanel's composer
 * toolbar). Click a row to insert it into the composer; hover reveals a
 * delete X; the "+ Add" field at the bottom appends a new snippet. Full
 * management (edit / drag-reorder / use counts) lives in the ProfileDialog
 * "Quick Replies" pane.
 *
 * Props:
 *  - quickReplies           : [{ id, text, uses, createdAt }] — the ACKED
 *                             server list from App state; always rendered
 *                             as-is (order is user-controlled).
 *  - onQuickRepliesChange(nextList) : Promise — persists a full-list replace;
 *                             rejects with { message } on a server error.
 *  - onInsert(reply)        : insert the snippet into the composer + close.
 */
const QuickRepliesPopover = ({
  quickReplies = [],
  onQuickRepliesChange,
  onInsert,
}) => {
  const { classes } = useStyles();
  const [filter, setFilter] = useState('');
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const atCap = quickReplies.length >= QUICK_REPLY_MAX;
  const q = filter.trim().toLowerCase();
  const visible = q
    ? quickReplies.filter((r) => (r.text || '').toLowerCase().includes(q))
    : quickReplies;

  // Persist a full-list replace. Render always comes back from the acked list
  // via the quickReplies prop — no local copies of server fields.
  const mutate = (next) => {
    if (!onQuickRepliesChange) return;
    setError('');
    setBusy(true);
    onQuickRepliesChange(next)
      .catch((e) => setError(e?.message || 'Could not save'))
      .finally(() => setBusy(false));
  };

  const handleAdd = () => {
    const text = draft.trim();
    if (!text || busy || atCap) return;
    if (quickReplies.some((r) => r.text === text)) {
      setError('Already saved as a quick reply');
      return;
    }
    mutate([...quickReplies, { text }]);
    setDraft('');
  };

  return (
    <div className={classes.root}>
      {quickReplies.length > SEARCH_THRESHOLD && (
        <div className={classes.searchRow}>
          <SearchIcon />
          <InputBase
            className={classes.searchInput}
            placeholder="Filter quick replies"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
      )}

      <ul className={classes.list}>
        {visible.length === 0 && (
          <li className={classes.empty}>
            {quickReplies.length === 0
              ? 'No quick replies yet — add one below.'
              : 'No matches.'}
          </li>
        )}
        {visible.map((reply) => (
          <li key={reply.id} className={classes.row}>
            <button
              type="button"
              className={classes.rowText}
              title={reply.text}
              onClick={() => onInsert && onInsert(reply)}
            >
              {reply.text}
            </button>
            <IconButton
              className={`${classes.deleteBtn} qr-delete`}
              size="small"
              disabled={busy}
              title="Delete quick reply"
              aria-label={`Delete quick reply: ${reply.text}`}
              onClick={() => mutate(quickReplies.filter((r) => r.id !== reply.id))}
            >
              <CloseIcon style={{ fontSize: '0.9rem' }} />
            </IconButton>
          </li>
        ))}
      </ul>

      <TextField
        className={classes.addRow}
        fullWidth
        size="small"
        variant="outlined"
        placeholder={atCap ? undefined : '+ Add a quick reply…'}
        value={draft}
        disabled={busy || atCap}
        helperText={atCap ? `${QUICK_REPLY_MAX}/${QUICK_REPLY_MAX} — remove one first` : undefined}
        slotProps={{ htmlInput: { maxLength: QUICK_REPLY_MAX_LEN } }}
        onChange={(e) => {
          setDraft(e.target.value);
          if (error) setError('');
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleAdd();
          }
        }}
      />
      {error && <div className={classes.error}>{error}</div>}

      <Typography variant="caption" className={classes.hint}>
        Tip: type / in the message box
      </Typography>
    </div>
  );
};

export default QuickRepliesPopover;
